/**
 * Presentational SEO content block shared by tool and conversion landing pages:
 * H1, intro, how-to, features, FAQ, internal links, plus inline JSON-LD.
 * Clean, compact, professional card UI with a link to /blog for deep articles.
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { BookOpen, ChevronDown, ChevronUp, ExternalLink } from "lucide-react";
import type { Locale } from "@/seo/seo.config";

export interface SeoArticleProps {
  h1: string;
  intro: string;
  howTo: { name: string; steps: { name: string; text: string }[] };
  features: string[];
  faq: { q: string; a: string }[];
  related: { label: string; href: string }[];
  /** JSON-LD @graph object, stringified into a ld+json script. */
  schema: object;
  /** Active UI locale, so the fixed section labels follow the language toggle. */
  locale: Locale;
}

const LABELS: Record<Locale, { about: string; features: string; faq: string; related: string; blogLink: string; details: string }> = {
  en: {
    about: "Tool Info & Guide",
    features: "Features",
    faq: "FAQ",
    related: "Related Tools",
    blogLink: "Read full guide & articles on Blog",
    details: "Show technical details",
  },
  id: {
    about: "Panduan & Informasi Alat",
    features: "Fitur Unggulan",
    faq: "Tanya Jawab (FAQ)",
    related: "Alat Terkait",
    blogLink: "Baca panduan lengkap & tutorial di Blog",
    details: "Tampilkan detail teknis",
  },
};

const labelStyle: React.CSSProperties = {
  fontSize: 9,
  fontWeight: 700,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: "hsl(var(--brand-strong))",
  marginBottom: 6,
};

const SeoArticle = ({ h1, intro, howTo, features, faq, related, schema, locale }: SeoArticleProps) => {
  const navigate = useNavigate();
  const [showDetails, setShowDetails] = useState(false);
  const lab = LABELS[locale] ?? LABELS.en;

  return (
    <section
      aria-label={lab.about}
      style={{
        marginTop: 18,
        paddingTop: 14,
        borderTop: "1px solid hsl(var(--border) / 0.6)",
      }}
    >
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
      />

      {/* Clean compact card header */}
      <div
        style={{
          background: "hsl(var(--accent) / 0.35)",
          border: "1px solid hsl(var(--border) / 0.8)",
          borderRadius: 8,
          padding: "10px 12px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
          <h1 style={{ fontSize: 13, fontWeight: 800, color: "hsl(var(--foreground))", margin: 0, lineHeight: 1.3 }}>
            {h1}
          </h1>
          <button
            onClick={() => navigate("/blog")}
            style={{
              display: "flex", alignItems: "center", gap: 4,
              fontSize: 10, fontWeight: 700,
              color: "hsl(var(--brand-strong))",
              background: "none", border: "none", cursor: "pointer", padding: 0,
            }}
          >
            <BookOpen style={{ width: 12, height: 12 }} />
            Blog
            <ExternalLink style={{ width: 10, height: 10 }} />
          </button>
        </div>

        <p style={{ fontSize: 11, color: "hsl(var(--muted-foreground))", lineHeight: 1.45, margin: "0 0 8px" }}>
          {intro}
        </p>

        {/* Quick link to blog panel & toggle details */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, paddingTop: 6, borderTop: "1px solid hsl(var(--border) / 0.5)" }}>
          <a
            href="/blog"
            onClick={(e) => { e.preventDefault(); navigate("/blog"); }}
            style={{ fontSize: 10, fontWeight: 700, color: "hsl(var(--brand-strong))", textDecoration: "none" }}
          >
            {lab.blogLink} →
          </a>

          <button
            onClick={() => setShowDetails((v) => !v)}
            aria-expanded={showDetails}
            aria-controls="seo-article-details"
            style={{
              display: "flex", alignItems: "center", gap: 3,
              fontSize: 9, fontWeight: 700, color: "hsl(var(--muted-foreground))",
              background: "none", border: "none", cursor: "pointer", padding: 0,
            }}
          >
            {lab.details}
            {showDetails ? <ChevronUp style={{ width: 11, height: 11 }} /> : <ChevronDown style={{ width: 11, height: 11 }} />}
          </button>
        </div>
      </div>

      {/* Expandable technical details (HowTo, Features, FAQ, Related).
          Always rendered, only collapsed visually: the JSON-LD above declares
          HowTo + FAQPage, and Google requires that marked-up content to be
          present on the page. Unmounting it would leave the schema describing
          content that isn't in the DOM. */}
      <div
        id="seo-article-details"
        style={{
          overflow: "hidden",
          maxHeight: showDetails ? 3000 : 0,
          opacity: showDetails ? 1 : 0,
          transition: "max-height 300ms ease, opacity 200ms ease",
        }}
      >
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 12, fontSize: 11 }}>
          {howTo?.steps?.length > 0 && (
            <div>
              <p style={labelStyle}>{howTo.name}</p>
              <ol style={{ margin: 0, padding: 0, listStyle: "none" }}>
                {howTo.steps.map((s, i) => (
                  <li key={s.name} style={{ display: "flex", gap: 6, marginBottom: 4, fontSize: 10.5, lineHeight: 1.4 }}>
                    <span style={{ fontWeight: 800, color: "hsl(var(--brand-strong))" }}>{i + 1}.</span>
                    <span><strong style={{ color: "hsl(var(--foreground))" }}>{s.name}:</strong> {s.text}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {features?.length > 0 && (
            <div>
              <p style={labelStyle}>{lab.features}</p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {features.map((f) => (
                  <span key={f} style={{ fontSize: 9.5, fontWeight: 600, padding: "2px 7px", borderRadius: 12, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))", color: "hsl(var(--foreground))" }}>
                    {f}
                  </span>
                ))}
              </div>
            </div>
          )}

          {faq?.length > 0 && (
            <div>
              <p style={labelStyle}>{lab.faq}</p>
              {faq.map((f) => (
                <div key={f.q} style={{ marginBottom: 6 }}>
                  <p style={{ fontSize: 10.5, fontWeight: 700, color: "hsl(var(--foreground))", margin: 0 }}>{f.q}</p>
                  <p style={{ fontSize: 10, color: "hsl(var(--muted-foreground))", margin: 0, lineHeight: 1.4 }}>{f.a}</p>
                </div>
              ))}
            </div>
          )}

          {related?.length > 0 && (
            <div>
              <p style={labelStyle}>{lab.related}</p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {related.map((r) => (
                  <a
                    key={r.href}
                    href={r.href}
                    onClick={(e) => { e.preventDefault(); navigate(r.href); }}
                    style={{ fontSize: 9.5, fontWeight: 700, padding: "3px 8px", borderRadius: 4, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))", color: "hsl(var(--brand-strong))", textDecoration: "none" }}
                  >
                    {r.label} →
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default SeoArticle;
